"use client";

import { type JSX, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useConsent } from "lib/consent/useConsent";

declare global {
  interface Window {
    cxone?: ((...args: unknown[]) => void) & {
      q?: unknown[];
      u?: string | number;
    };
    CXoneDfo?: string;
    [key: string]: unknown;
  }
}

type CxoneFn = ((...args: unknown[]) => void) & {
  q?: unknown[];
  u?: string | number;
};

const ChatbotScripts = (): JSX.Element => {
  const pathname = usePathname();
  const hasConsent = useConsent("functional");
  useEffect(() => {
    if (!hasConsent) return;

    const guide = process.env.NEXT_PUBLIC_CHATBOT_GUIDE;
    const chatbotEnabled = process.env.NEXT_PUBLIC_CHATBOT_ENABLED;
    const debug = process.env.NEXT_PUBLIC_CHATBOT_DEBUG === "true";

    const showChatbot = (): void => {
      if (typeof window === "undefined") return;
      if (document.querySelectorAll(".nicechatbot").length > 0) {
        const selectors = [
          '[id*="be-chat-container"]',
          '[id*="cxone-guide-container"]',
        ];

        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = "";
          });
        });
      }
    };

    const hideChatbot = (): void => {
      if (typeof window === "undefined") return;
      if (document.querySelectorAll(".nicechatbot").length == 0) {
        const selectors = [
          '[id*="be-chat-container"]',
          '[id*="cxone-guide-container"]',
        ];
        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = "none";
          });
        });
      }
    };

    const handleClick = (): void => {
      if (typeof window === "undefined") return;

      const modules =
        "https://web-modules-de-na1.niceincontact.com/loader/1/loader.js";
      const init = "1488";

      if (chatbotEnabled !== "true") {
        return;
      }

      // Show chatbot again if it was hidden during navigation
      showChatbot();

      if (window.cxone) {
        window.cxone("guide", "openEntrypoint", guide);
        return;
      }

      const win = window as Window & Record<string, CxoneFn | undefined>;
      ((n: string, u: string) => {
        window.CXoneDfo = n;
        let fn = win[n] as CxoneFn | undefined;
        if (!fn) {
          fn = function (...args: unknown[]) {
            (fn!.q = fn!.q || []).push(args);
          } as CxoneFn;
          fn.q = [];
          win[n] = fn;
        }
        fn.u = u;
        const e = document.createElement("script");
        e.type = "module";
        e.src = `${u}?${Math.round(Date.now() / 1e3 / 3600)}`;
        document.head.appendChild(e);
      })("cxone", modules);

      const cxone = window.cxone as ((...args: unknown[]) => void) | undefined;
      cxone?.("init", init);
      cxone?.("guide", "init");
      cxone?.("guide", "openEntrypoint", guide);
    };

    const buttons = document.querySelectorAll(".nicechatbot");
    buttons.forEach((btn) => btn.addEventListener("click", handleClick));

    return () => {
      buttons.forEach((btn) => btn.removeEventListener("click", handleClick));
      // App Router has no `routeChangeStart` event; instead the effect
      // re-runs whenever `pathname` changes, and we tear down the previous
      // chatbot widget here so it does not bleed into the next route.
      hideChatbot();
    };
  }, [pathname, hasConsent]);

  return <></>;
};

export default ChatbotScripts;
